/**
 * Shared constants for the AI Insights feature. Prompt text, poll timings and protocol
 * literals are centralised here so call sites stay declarative and the agent contract is
 * changed in exactly one place.
 */

/** Report types accepted by the agent as conversation context. */
export const AI_INSIGHT_REPORT_TYPES = {
    TP_TAX: 'TP_TAX',
    VARIANCE: 'VAR',
} as const;

/**
 * userId the reporting service records against reports triggered by the agent's
 * GenerateReport action group. Used when polling for completion.
 */
export const AGENT_REPORT_USER_ID = 'bedrock-agent';

/** WebSocket message types emitted by the AI Insights agent API. */
export const AI_INSIGHT_MESSAGE_TYPES = {
    INDEXING: 'indexing',
    CHUNK: 'chunk',
    COMPLETE: 'complete',
    ERROR: 'error',
} as const;

/** Prefix on the transient "indexing" notice, so the reducer can tell it from real content. */

/** Interval between report-status polls while waiting for an agent-triggered report. */
export const REPORT_POLL_INTERVAL_MS = 10_000;

/** Upper bound on report-status polling before the user is told to check manually. */
export const REPORT_POLL_TIMEOUT_MS = 5 * 60 * 1000;

/**
 * Idle window after the last streamed chunk before the response is treated as complete.
 * The agent API does not always emit a terminal `complete` frame, so a quiet period is
 * used as a fallback so the UI never hangs in a loading state.
 */
export const STREAM_IDLE_COMPLETE_MS = 5_000;

/** Report-status values that mean the report is queryable by the agent. */
export const REPORT_STATUS_READY = ['SUCCESS', 'COMPLETED'];

/** Report-status value that means generation will not complete. */
export const REPORT_STATUS_FAILED = 'FAILED';

/** Prompt sent automatically once an agent-triggered report finishes generating. */
export const REPORT_READY_FOLLOW_UP_PROMPT =
    'The report has been generated successfully. Now retrieve the latest TP TAX report from '
    + 'ATPReportsKB for the CLI and period I just generated and summarize the key findings '
    + 'including P&L totals, markup percentage, target vs actual, and any anomalies.';

/**
 * Opening prompt for the TP Tax (MAR) report page. Period, accounting day and CLI all come
 * from the report's current selection.
 */
export const buildTpTaxDefaultPrompt = (
    cliId: string,
    accountingPeriod: string,
    accountingDay: string
) => `Explain the data sources and calculation steps for the CLI: ${cliId} `
    + `for period ${accountingPeriod} and Accounting Day ${accountingDay}`;

/**
 * Opening prompt for a variance-report row drill-in. Period and accounting day come from the
 * report's current selection; the CLI is the one on the row.
 */
export const buildVarianceRootCausePrompt = (
    cliId: string,
    accountingPeriod: string,
    accountingDay: string
) => `Fetch the MAR report for period ${accountingPeriod} and Accounting Day ${accountingDay} `
    + `for CLI: ${cliId} and do the variance root cause analysis`;
